import { Button, Card, Empty, Input, Modal, Pagination, Select, Space, Tooltip, Typography, message } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Operation_History_Enum } from "@/meta/logistics/operationHistory";
import OperationHistoryPanel from "@/views/components/OperationHistoryPanel";
import { StatusTag } from "@/views/components/StatusTag";
import { showRequestError } from "@/utils/requestErrorMessage";
import type {
    FindContractSpecialsItem,
    SpecialZoneContractGoods,
    SpecialZoneContractPagingRow,
    SpecialZoneContractPriceAdjustLog
} from "@zhihaoscm/entity";
import {
    ZONE_CONTRACT_GOODS,
    ZONE_CONTRACT_RESERVATION_ORDER_STATE,
    ZONE_CONTRACT_SPECIAL_ZONE,
    ZONE_CONTRACT_STATE
} from "@zhihaoscm/meta";
import { useUserStore } from "@/views/stores";
import AssignDialog, { type assignSubmitData } from "@/views/components/AssignDialog/AssignDialog";
import {
    DateRangePicker as ZhDateRangePicker,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";
import type { ZoneContractReservationResult, ZoneContractReservationRow } from "./zoneContractReactTypes";
import { zoneContractApi } from "./zoneContractReactApi";
import {
    goodsIntroByGoodsCode,
    reservationOrderStateLabel,
    displayApiDateTimeUnknown
} from "../zoneReservationOrder/zoneReservationDisplay";
import { displayZoneContractCustomerText } from "./zoneContractCustomerLabel";
import { mapContractSpecialsToOfficers } from "./zoneContractAssign";
import { auditZoneReservationOrder, cancelZoneReservationOrder } from "../ZoneReservationOrderDetailPage";
import { openShippingShipDetail, openShippingTransportDetail } from "@/utils/shippingAdminNavigation";
import notDataImage from "@/views/assets/images/notData.png";

export interface ZoneContractDetailPageProps {
    contractId?: string;
    loadDetail?: (id: number) => Promise<SpecialZoneContractPagingRow>;
    loadReservations?: (params: {
        contractId: number;
        page: number;
        size: number;
    }) => Promise<ZoneContractReservationResult>;
    loadPriceLogs?: (params: {
        contractId: number;
        page: number;
        size: number;
    }) => Promise<{ list?: SpecialZoneContractPriceAdjustLog[]; total?: number }>;
    cancelContract?: (id: number) => Promise<void>;
    permissions?: string[];
}

const loadDetailDefault = (id: number) => zoneContractApi.detail(id);
const loadReservationsDefault = (params: { contractId: number; page: number; size: number }) =>
    zoneContractApi.reservations(params);
const loadPriceLogsDefault = (params: { contractId: number; page: number; size: number }) =>
    zoneContractApi.priceLogs(params);

const PRE_OCCUPIED_TIP = "预占金额：预约单提交后即预占对应吨数的金额，提货完成后释放并正式扣除专区余额";

/** 空日志沿用旧 Vue 的六列表头，避免 ZhTable 默认空态重复渲染“暂无数据”。 */
const PRICE_LOG_EMPTY_HEADERS = ["物料名称", "原单价（元）", "新单价（元）", "单价生效时间", "操作人", "操作时间"];

/** 预约明细沿用 Vue 的合并列：预约头字段、状态、创建时间和操作列按预约单合并。 */
const BOOKING_MERGED_COLUMN_INDEXES = new Set([0, 1, 2, 3, 11, 12, 13]);

function reservationCore(row: ZoneContractReservationRow) {
    return row.specialZoneReservationOrder || row;
}

/** 预约单接口一行可能携带多个物料，详情表按旧 Vue 表格拆成一行一物料。 */
function flattenReservationRows(rows: ZoneContractReservationRow[]): ZoneContractReservationRow[] {
    return rows.flatMap(row => {
        const goods = row.specialZoneReservationOrderGoods;
        if (!Array.isArray(goods) || goods.length <= 1) return [row];
        return goods.map((item, index) => ({
            ...row,
            __isContinuation: index > 0,
            specialZoneReservationOrderGoods: [item]
        }));
    });
}

/** 计算当前预约单连续物料行的 rowspan，避免多物料预约单重复渲染公共字段。 */
export function reservationRowSpanFor(rows: ZoneContractReservationRow[], rowIndex: number): number {
    const current = rows[rowIndex];
    if (!current || current.__isContinuation) return 0;

    const currentId = reservationText(current, "id");
    if (!currentId) return 1;

    let rowSpan = 1;
    for (let index = rowIndex + 1; index < rows.length; index += 1) {
        if (reservationText(rows[index], "id") !== currentId) break;
        rowSpan += 1;
    }
    return rowSpan;
}

/** 物料信息合计只返回 Vue 要求的合同量、完成量、结算吨数和结算金额。 */
export function buildZoneContractMaterialSummary(
    columns: Array<{ prop?: string; property?: string }>,
    data: Array<Pick<SpecialZoneContractGoods, "completedTon" | "settlementTon" | "settlementAmount">>,
    goodsTotalTon: number
): string[] {
    const completedTon = data.reduce((sum, row) => sum + Number(row.completedTon || 0), 0);
    const settlementTon = data.reduce((sum, row) => sum + Number(row.settlementTon || 0), 0);
    const settlementAmount = data.reduce((sum, row) => sum + Number(row.settlementAmount || 0), 0);

    return columns.map(column => {
        const property = String(column.prop || column.property || "");
        if (property === "goodsName") return "合计";
        if (property === "totalTon") return formatNumber(goodsTotalTon);
        if (property === "completedTon") return formatNumber(completedTon);
        if (property === "settlementTon") return formatNumber(settlementTon);
        if (property === "settlementAmount") return settlementAmount.toFixed(2);
        return "--";
    });
}

function isCancelledState(value: unknown): boolean {
    // 预约单和合同使用不同状态枚举；Vue 端预约取消状态为 5，不能复用合同取消状态 2。
    if (typeof value !== "string" && typeof value !== "number") return false;
    return ZONE_CONTRACT_RESERVATION_ORDER_STATE.CANCELLED.match(value);
}

function contractStateLabel(value: unknown): string {
    const code = Number(value);
    return Number.isFinite(code) ? ZONE_CONTRACT_STATE.getIntroByCode(code) || "--" : "--";
}

function numberValue(value: unknown): number | undefined {
    if (value == null || value === "") return undefined;
    const result = Number(value);
    return Number.isFinite(result) ? result : undefined;
}

function formatNumber(value: unknown, maximumFractionDigits = 6): string {
    const result = numberValue(value);
    return result == null ? "--" : result.toLocaleString("zh-CN", { maximumFractionDigits, minimumFractionDigits: 0 });
}

/** 预约明细结算率沿用旧 Vue 的两位小数格式，避免 0.10 被压缩成 0.1。 */
function formatSettlementRate(value: unknown): string {
    const result = numberValue(value);
    return result == null
        ? "--"
        : result.toLocaleString("zh-CN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

/** 详情金额沿用 Vue DetailJson 的货币前缀和千分位展示。 */
function formatDetailMoney(value: unknown): string {
    const result = numberValue(value);
    return result == null
        ? "--"
        : `¥${result.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** 详情页采购用户沿用列表页的主体、实名、手机号和用户编码兜底展示。 */
function detailCustomerLabel(customer: SpecialZoneContractPagingRow["businessCustomer"] | undefined): string {
    if (customer?.institutionName || customer?.applyState != null) {
        return displayZoneContractCustomerText(customer);
    }
    return (
        [customer?.realName || customer?.institutionName || customer?.nickName, customer?.mobile, customer?.code]
            .filter(Boolean)
            .join(" | ") || "--"
    );
}

/** 详情字段使用与 Vue DetailJson 相同的固定标签宽度，避免 BaseItem 默认宽度挤压首行。 */
function DetailField({
    label,
    value,
    className = ""
}: {
    label: string;
    value: ReactNode;
    className?: string;
}) {
    return (
        <div className={`zone-contract-detail-field ${className}`.trim()}>
            <span className="zone-contract-detail-field__label">{label}</span>
            <span className="zone-contract-detail-field__value">{value}</span>
        </div>
    );
}

function reservationGoods(row: ZoneContractReservationRow): Record<string, unknown> {
    const value = row.specialZoneReservationOrderGoods;
    return Array.isArray(value) && value.length && typeof value[0] === "object"
        ? (value[0] as Record<string, unknown>)
        : {};
}

function reservationValue(row: ZoneContractReservationRow, key: string): unknown {
    return (reservationCore(row) as Record<string, unknown>)[key] ?? row[key];
}

/** 预约明细链接字段兼容嵌套预约单和扁平接口返回。 */
function reservationText(row: ZoneContractReservationRow, key: string): string {
    return String(reservationValue(row, key) ?? "").trim();
}

/** 预约物料字段兼容旧接口和过磅接口的多种装货吨数命名。 */
function reservationGoodsNumber(row: ZoneContractReservationRow, keys: string[]): number | undefined {
    const goods = reservationGoods(row);
    for (const key of keys) {
        const value = numberValue(goods[key]);
        if (value != null) return value;
    }
    return undefined;
}

/** 合同详情：并行加载合同和预约明细，价格日志严格在显式点击后请求。 */
export function ZoneContractDetailPage({
    contractId: contractIdProp,
    loadDetail = loadDetailDefault,
    loadReservations = loadReservationsDefault,
    loadPriceLogs = loadPriceLogsDefault,
    cancelContract = zoneContractApi.cancel,
    permissions
}: ZoneContractDetailPageProps) {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    const canManage = currentPermissions.includes("ROLE_ZONE_CONTRACT_MANAGE");
    const canDeal = currentPermissions.includes("ROLE_ZONE_CONTRACT_DEAL");
    // Vue 详情路由通过 query.id 传递合同编号，同时兼容 React 路径参数和测试注入值。
    const id = Number(contractIdProp ?? params.id ?? new URLSearchParams(location.search).get("id"));
    const [detail, setDetail] = useState<SpecialZoneContractPagingRow>();
    const [reservations, setReservations] = useState<ZoneContractReservationRow[]>([]);
    const [error, setError] = useState<string>();
    const [cancelOpen, setCancelOpen] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);
    const [priceOpen, setPriceOpen] = useState(false);
    const [logs, setLogs] = useState<SpecialZoneContractPriceAdjustLog[]>([]);
    const [logKeyword, setLogKeyword] = useState("");
    const [logMaterial, setLogMaterial] = useState<string>();
    const [logDateRange, setLogDateRange] = useState<[string | null, string | null]>([null, null]);
    const [logPage, setLogPage] = useState(1);
    const [logPageSize, setLogPageSize] = useState(10);
    const [officers, setOfficers] = useState<FindContractSpecialsItem[]>([]);
    const [handlerId, setHandlerId] = useState<number>();
    const assignOfficers = useMemo(() => mapContractSpecialsToOfficers(officers), [officers]);

    useEffect(() => {
        if (!Number.isFinite(id) || id <= 0) {
            setError("缺少有效的合同 ID");
            return;
        }
        let disposed = false;
        // 合同主体和预约明细分别降级，避免任一网络错误阻断另一块页面内容。
        void loadDetail(id)
            .then(contract => {
                if (!disposed) setDetail(contract);
            })
            .catch(() => {
                if (!disposed) setError(current => current ?? "加载合同详情失败");
            });
        void loadReservations({ contractId: id, page: 1, size: 1000 })
            .then(booking => {
                if (!disposed) setReservations(flattenReservationRows(booking.list || []));
            })
            .catch(() => {
                if (!disposed) setError(current => current ?? "加载预约明细失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail, loadReservations]);

    const core = detail?.specialZoneContract;
    const cancelled = isCancelledState(core?.state);
    const goods = (detail?.specialZoneContractGoods || []).filter(
        row => row.goodsCode !== 0 && row.goodsName !== "汇总"
    );
    const goodsTotalTon = goods.reduce((total, row) => total + Number(row.totalTon || 0), 0);
    const filteredLogs = logs.filter(row => {
        const material = row.goodsName || ZONE_CONTRACT_GOODS.getIntroByCode(row.goodsCode) || "--";
        const keywordMatch = !logKeyword.trim() || (row.operatorName || "").includes(logKeyword.trim());
        const materialMatch = !logMaterial || material === logMaterial;
        const startMatch = !logDateRange[0] || String(row.operationTime || "").slice(0, 10) >= logDateRange[0];
        const endMatch = !logDateRange[1] || String(row.operationTime || "").slice(0, 10) <= logDateRange[1];
        return keywordMatch && materialMatch && startMatch && endMatch;
    });
    // 合同预约明细沿用 Vue 字段映射，避免改变预约单状态和船舶展示文案。
    const reservationColumns: TableColumnProps<ZoneContractReservationRow>[] = [
        {
            label: "预约单编号",
            width: 140,
            prop: "id",
            render: ({ row }) => {
                if (row.__isContinuation) return "";
                const id = reservationText(row, "id");
                return id ? (
                    <Button
                        type="link"
                        className="zone-contract-detail-link"
                        onClick={() => navigate(`/tradeManage/zoneReservationOrderDetail?id=${encodeURIComponent(id)}`)}
                    >
                        {id}
                    </Button>
                ) : (
                    "--"
                );
            }
        },
        {
            label: "船名",
            width: 110,
            prop: "ship",
            render: ({ row }) => {
                if (row.__isContinuation) return "";
                const shipName =
                    reservationText(row, "shipName") ||
                    (row.ship as { cnname?: string; name?: string } | undefined)?.cnname ||
                    "--";
                const shipId = reservationText(row, "shipId");
                return shipId ? (
                    <Button
                        type="link"
                        className="zone-contract-detail-link"
                        onClick={() => {
                            if (!openShippingShipDetail(shipId)) message.error("未配置船务后台地址");
                        }}
                    >
                        {shipName}
                    </Button>
                ) : (
                    shipName
                );
            }
        },
        {
            label: "船运单编号",
            width: 130,
            prop: "transport",
            render: ({ row }) => {
                if (row.__isContinuation) return "";
                const transportOrderShipId = reservationText(row, "transportOrderShipId");
                const requirementPlatId = reservationText(row, "requirementPlatId");
                const transportId = transportOrderShipId || requirementPlatId;
                return transportId ? (
                    <Button
                        type="link"
                        className="zone-contract-detail-link"
                        onClick={() => {
                            if (!openShippingTransportDetail(transportOrderShipId, requirementPlatId)) {
                                message.error("未配置船务后台地址");
                            }
                        }}
                    >
                        {transportId}
                    </Button>
                ) : (
                    "--"
                );
            }
        }
    ];
    // 单价调整日志只在用户打开弹窗后加载，表格保留旧 Vue 的价格和时间列。
    const logColumns: TableColumnProps<SpecialZoneContractPriceAdjustLog>[] = [
        {
            label: "物料名称",
            width: 234,
            prop: "goods",
            render: ({ row }) => row.goodsName || ZONE_CONTRACT_GOODS.getIntroByCode(row.goodsCode) || "--"
        },
        {
            label: "原单价（元）",
            width: 109,
            prop: "oldUnitPrice",
            render: ({ row }) => formatNumber(row.oldUnitPrice)
        },
        {
            label: "新单价（元）",
            width: 109,
            prop: "newUnitPrice",
            render: ({ row }) => formatNumber(row.newUnitPrice)
        },
        {
            label: "单价生效时间",
            width: 159,
            prop: "effectiveTime",
            render: ({ row }) => displayApiDateTimeUnknown(row.effectiveTime)
        },
        { label: "操作人", width: 114, prop: "operatorName", render: ({ row }) => row.operatorName || "--" },
        {
            label: "操作时间",
            width: 149,
            prop: "operationTime",
            render: ({ row }) => displayApiDateTimeUnknown(row.operationTime)
        }
    ];

    async function openAssign() {
        try {
            setOfficers(await zoneContractApi.specials());
            setHandlerId(core?.handlerId || undefined);
            setAssignOpen(true);
        } catch (error) {
            showRequestError(error, "加载合同专员列表失败");
        }
    }
    async function submitAssign({ officerId }: assignSubmitData) {
        if (core?.id == null || officerId == null) return;
        try {
            await zoneContractApi.assign(core.id, Number(officerId));
            setAssignOpen(false);
            setHandlerId(undefined);
            message.success("指派成功");
        } catch (error) {
            showRequestError(error, "指派失败");
        }
    }

    /** 取消合同前重新按旧 Vue 的 100 条分页校验，避免详情首屏数据不足导致误取消。 */
    async function hasActiveReservation(): Promise<boolean> {
        if (core?.id == null) return false;
        let page = 1;
        while (true) {
            const result = await loadReservations({ contractId: core.id, page, size: 100 });
            const rows = result.list || [];
            if (rows.some(row => !isCancelledState(reservationValue(row, "state")))) return true;
            if (rows.length < 100) return false;
            page += 1;
        }
    }

    async function requestCancel() {
        if (core?.id == null) return;
        let blocked: boolean;
        try {
            blocked = await hasActiveReservation();
        } catch (error) {
            showRequestError(error, "校验失败，请稍后重试");
            return;
        }
        if (blocked) {
            message.warning("合同下已有预约单，无法取消，请先取消预约单！");
            return;
        }
        setCancelOpen(true);
    }

    async function confirmCancel() {
        if (core?.id == null) return;
        try {
            await cancelContract(core.id);
            setCancelOpen(false);
            setDetail(current =>
                current ? { ...current, specialZoneContract: { ...current.specialZoneContract, state: 2 } } : current
            );
        } catch (error) {
            showRequestError(error, "取消合同失败");
        }
    }
    async function openPriceLogs() {
        if (core?.id == null) return;
        setPriceOpen(true);
        setLogPage(1);
        setLogPageSize(10);
        try {
            const result = await loadPriceLogs({ contractId: core.id, page: 1, size: 20 });
            setLogs(result.list || []);
        } catch (error) {
            showRequestError(error, "加载单价调整日志失败");
        }
    }

    /** 预约明细操作列按旧 Vue 的预约单状态保留可见操作入口。 */
    function renderReservationOperations(row: ZoneContractReservationRow) {
        const reservationId = reservationCore(row).id;
        if (cancelled || reservationId == null || !canDeal) return null;
        const state = Number(reservationValue(row, "state"));
        if (![1, 2, 3, 4].includes(state)) return null;
        const editLabel = state === 3 || state === 4 ? "编辑过磅单" : "编辑";
        return (
            <Space className="zone-contract-operation-cell" size={4}>
                <Button
                    type="link"
                    onClick={() => navigate(`/tradeManage/zoneReservationOrderDetail?id=${reservationId}`)}
                >
                    {editLabel}
                </Button>
                {state === 2 ? (
                    <>
                        <Button
                            type="link"
                            onClick={() => void handleReservationAction(String(reservationId), "cancel")}
                        >
                            取消
                        </Button>
                        <Button
                            type="link"
                            onClick={() => navigate(`/tradeManage/zoneReservationOrderDetail?id=${reservationId}`)}
                        >
                            磅单录入
                        </Button>
                    </>
                ) : null}
                {state === 1 ? (
                    <>
                        <Button
                            type="link"
                            onClick={() => void handleReservationAction(String(reservationId), "cancel")}
                        >
                            取消
                        </Button>
                        <Button
                            type="link"
                            onClick={() => void handleReservationAction(String(reservationId), "audit")}
                        >
                            审核
                        </Button>
                    </>
                ) : null}
            </Space>
        );
    }

    /** 预约明细操作列复用预约详情页的审核/取消接口，按钮不再是仅展示的空操作。 */
    async function handleReservationAction(id: string, action: "audit" | "cancel"): Promise<void> {
        try {
            if (action === "audit") await auditZoneReservationOrder(id);
            else await cancelZoneReservationOrder(id);
            message.success(action === "audit" ? "审核成功" : "取消成功");
            setReservations(current =>
                current.map(row => {
                    if (String(reservationCore(row).id) !== id) return row;
                    const nextState = action === "audit" ? 2 : 5;
                    return row.specialZoneReservationOrder
                        ? {
                              ...row,
                              specialZoneReservationOrder: {
                                  ...row.specialZoneReservationOrder,
                                  state: nextState
                              }
                          }
                        : { ...row, state: nextState };
                })
            );
        } catch (error) {
            showRequestError(error, action === "audit" ? "审核失败" : "取消失败");
        }
    }

    return (
        <section aria-label="zone-contract-detail-page">
            {error ? <Alert message={error} type="error" /> : null}
            {!detail && !error ? (
                <Card loading />
            ) : (
                <>
                    <div className="section-card zone-contract-header" data-testid="zhihao-grid">
                        <span className="sr-only">
                            合同编号: {core?.contractCode || "--"} 采购用户: {detailCustomerLabel(detail?.businessCustomer)}
                        </span>
                        <div className="zone-contract-header__row zone-contract-header__row--primary">
                            <div className="zone-contract-header__title">
                                <div className="section-title mb-0">合同信息</div>
                                <DetailField label="合同编号" value={core?.contractCode || "--"} />
                            </div>
                            <div className="zone-contract-header__right">
                                <DetailField label="签订日期" value={core?.signDate || "--"} />
                                <DetailField label="创建时间​" value={displayApiDateTimeUnknown(core?.createdTime)} />
                                <DetailField label="合同专员" value={core?.handlerName || "--"} />
                                {core ? (
                                    <StatusTag
                                        className={`zone-contract-header__status ${cancelled ? "is-cancelled" : ""}`}
                                        tone={cancelled ? "info" : "success"}
                                    >
                                        {contractStateLabel(core.state)}
                                    </StatusTag>
                                ) : null}
                            </div>
                        </div>
                        <div className="zone-contract-header__row zone-contract-header__row--details">
                            <DetailField
                                className="zone-contract-header__buyer"
                                label="采购用户"
                                value={detailCustomerLabel(detail?.businessCustomer)}
                            />
                            <DetailField
                                label="专区名称"
                                value={
                                    ZONE_CONTRACT_SPECIAL_ZONE.getIntroByCode(core?.specialZoneId) ||
                                    String(core?.specialZoneId ?? "--")
                                }
                            />
                            <DetailField
                                label="合同日期"
                                value={`${core?.pickStartDate || "--"} ～ ${core?.pickEndDate || "--"}`}
                            />
                        </div>
                        <div className="zone-contract-header__row zone-contract-header__row--amounts">
                            <DetailField
                                label="专区余额"
                                value={formatDetailMoney(detail?.specialZoneAccount?.balanceAmount)}
                            />
                            <DetailField
                                label="预占金额"
                                value={
                                    <span className="zone-contract-pre-occupied-amount">
                                        {formatDetailMoney(core?.occAmount ?? detail?.specialZoneAccount?.occAmount)}
                                        <Tooltip title={PRE_OCCUPIED_TIP} placement="top">
                                            <i aria-hidden="true" className="iconfont icon-a-Frame38" />
                                        </Tooltip>
                                    </span>
                                }
                            />
                        </div>
                    </div>
                    <div className="section-card zone-contract-material">
                        <div className="zone-contract-material__title">
                            <Typography.Title level={5}>物料信息</Typography.Title>
                            <Button disabled={!core?.id} type="link" onClick={() => void openPriceLogs()}>
                                单价调整日志
                            </Button>
                        </div>
                        <ZhTable
                            border
                            rowKey={row => String(row.id || row.goodsCode)}
                            data={goods}
                            pagination={false}
                            showSummary
                            sumText="合计"
                            columns={[
                                { label: "物料名称", minWidth: 206, prop: "goodsName" },
                                {
                                    label: "合同量（吨）",
                                    minWidth: 178,
                                    prop: "totalTon",
                                    render: ({ row }) => (
                                        <span className="zone-contract-number-cell">{formatNumber(row.totalTon)}</span>
                                    )
                                },
                                {
                                    label: "完成量（吨）",
                                    minWidth: 212,
                                    prop: "completedTon",
                                    render: ({ row }) => (
                                        <span className="zone-contract-number-cell">
                                            {formatNumber(row.completedTon)}
                                        </span>
                                    )
                                },
                                {
                                    label: "结算率",
                                    minWidth: 188,
                                    prop: "settlementRate",
                                    render: ({ row }) => (
                                        <span className="zone-contract-number-cell">
                                            {numberValue(row.settlementRate)?.toFixed(2) || "--"}
                                        </span>
                                    )
                                },
                                {
                                    label: "结算吨数（吨）",
                                    minWidth: 192,
                                    prop: "settlementTon",
                                    render: ({ row }) => (
                                        <span className="zone-contract-number-cell">
                                            {formatNumber(row.settlementTon)}
                                        </span>
                                    )
                                },
                                {
                                    label: "结算金额（元）",
                                    minWidth: 163,
                                    prop: "settlementAmount",
                                    render: ({ row }) => (
                                        <span className="zone-contract-number-cell">
                                            {numberValue(row.settlementAmount)?.toFixed(2) || "--"}
                                        </span>
                                    )
                                },
                                {
                                    label: "配比",
                                    minWidth: 181,
                                    prop: "materialRatio",
                                    render: ({ row }) => (
                                        <span className="zone-contract-number-cell">
                                            {goodsTotalTon > 0
                                                ? `${((Number(row.totalTon || 0) / goodsTotalTon) * 100).toFixed(2)}%`
                                                : "--"}
                                        </span>
                                    )
                                },
                                {
                                    label: "最新单价（元/吨）",
                                    width: 179,
                                    prop: "latestUnitPrice",
                                    render: ({ row }) => (
                                        <span className="zone-contract-number-cell">
                                            {formatNumber(row.latestUnitPrice ?? row.unitPrice)}
                                        </span>
                                    )
                                },
                                {
                                    label: "完成进度",
                                    minWidth: 236,
                                    prop: "completedSchedule",
                                    render: ({ row }) => {
                                        const progress = numberValue(row.completedSchedule);
                                        return (
                                            <span className="zone-contract-number-cell">
                                                {progress == null ? "--" : `${(progress * 100).toFixed(0)}%`}
                                            </span>
                                        );
                                    }
                                }
                            ]}
                            summaryMethod={({ columns, data }) =>
                                buildZoneContractMaterialSummary(
                                    columns.map(column => ({ prop: column.prop, property: column.property })),
                                    data,
                                    goodsTotalTon
                                )
                            }
                        />
                    </div>
                    <div className="section-card zone-contract-booking">
                        <Space>
                            <Typography.Title level={5}>预约明细</Typography.Title>
                        </Space>
                        <ZhTable
                            border
                            rowKey={row =>
                                `${String(reservationCore(row).id)}-${String(reservationGoods(row).goodsCode ?? "")}`
                            }
                            data={reservations}
                            empty={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />}
                            pagination={false}
                            columns={[
                                ...reservationColumns.slice(0, 3),
                                {
                                    label: "预到日期",
                                    width: 180,
                                    prop: "reservationTime",
                                    render: ({ row }) =>
                                        row.__isContinuation
                                            ? ""
                                            : displayApiDateTimeUnknown(reservationValue(row, "reservationTime"))
                                },
                                {
                                    label: "物料名称",
                                    width: 120,
                                    prop: "goodsCode",
                                    render: ({ row }) =>
                                        goodsIntroByGoodsCode(numberValue(reservationGoods(row).goodsCode))
                                },
                                {
                                    label: "计划量（吨）",
                                    width: 110,
                                    align: "right",
                                    headerAlign: "right",
                                    prop: "plannedTonnage",
                                    render: ({ row }) => formatNumber(reservationGoodsNumber(row, ["plannedTonnage"]))
                                },
                                {
                                    label: "装货吨数（吨）",
                                    width: 130,
                                    align: "right",
                                    headerAlign: "right",
                                    prop: "loadingTonnage",
                                    render: ({ row }) =>
                                        formatNumber(
                                            reservationGoodsNumber(row, [
                                                "grossWeight",
                                                "loadingTonnage",
                                                "loadedTonnage",
                                                "weighTon",
                                                "weightTon"
                                            ])
                                        )
                                },
                                {
                                    label: "结算率",
                                    width: 90,
                                    prop: "settlementRate",
                                    render: ({ row }) => formatSettlementRate(reservationGoods(row).settlementRate)
                                },
                                {
                                    label: "结算吨数（吨）",
                                    width: 120,
                                    align: "right",
                                    headerAlign: "right",
                                    prop: "settlementTon",
                                    render: ({ row }) => formatNumber(reservationGoods(row).settlementTon)
                                },
                                {
                                    label: "结算单价（元）",
                                    width: 120,
                                    align: "right",
                                    headerAlign: "right",
                                    prop: "unitPrice",
                                    render: ({ row }) => formatNumber(reservationGoods(row).unitPrice)
                                },
                                {
                                    label: "结算金额（元）",
                                    width: 120,
                                    align: "right",
                                    headerAlign: "right",
                                    prop: "settlementAmount",
                                    render: ({ row }) => formatNumber(reservationGoods(row).settlementAmount, 2)
                                },
                                {
                                    label: "预约单状态",
                                    width: 100,
                                    prop: "state",
                                    render: ({ row }) =>
                                        reservationOrderStateLabel(Number(reservationValue(row, "state")))
                                },
                                {
                                    label: "创建时间",
                                    width: 180,
                                    prop: "createdTime",
                                    render: ({ row }) =>
                                        row.__isContinuation
                                            ? ""
                                            : displayApiDateTimeUnknown(reservationValue(row, "createdTime"))
                                },
                                {
                                    label: "操作",
                                    // Vue 空预约表会将固定操作列压为 0；有预约数据时才显示并固定在右侧。
                                    fixed: "right",
                                    width: reservations.length ? 148 : 0,
                                    prop: "operation",
                                    render: ({ row }) =>
                                        row.__isContinuation ? null : renderReservationOperations(row)
                                }
                            ]}
                            // 使用与旧 Vue span-method 相同的合并范围，保留一单多物料的公共字段合并。
                            spanMethod={({ columnIndex, rowIndex }) =>
                                BOOKING_MERGED_COLUMN_INDEXES.has(columnIndex)
                                    ? { rowspan: reservationRowSpanFor(reservations, rowIndex) }
                                    : undefined
                            }
                            showSummary
                            sumText="合计"
                            summaryMethod={({ columns, data }) =>
                                columns.map(column => {
                                    const property = String(column.prop || column.property || "");
                                    if (property === "id") return "合计";
                                    if (property === "plannedTonnage") {
                                        return formatNumber(
                                            data.reduce(
                                                (sum, row) =>
                                                    sum + (reservationGoodsNumber(row, ["plannedTonnage"]) || 0),
                                                0
                                            )
                                        );
                                    }
                                    if (property === "loadingTonnage") {
                                        return formatNumber(
                                            data.reduce(
                                                (sum, row) =>
                                                    sum +
                                                    (reservationGoodsNumber(row, [
                                                        "grossWeight",
                                                        "loadingTonnage",
                                                        "loadedTonnage",
                                                        "weighTon",
                                                        "weightTon"
                                                    ]) || 0),
                                                0
                                            )
                                        );
                                    }
                                    return "--";
                                })
                            }
                        />
                    </div>
                    <div className="section-card zone-contract-history">
                        {core?.id != null ? (
                            <OperationHistoryPanel
                                bizNo={core.id}
                                emptyText="暂无历史记录"
                                hideFrame
                                module={Operation_History_Enum.ZONE_CONTRACT_DETAIL}
                                title="历史记录"
                            />
                        ) : (
                            <>
                                <div className="section-title">历史记录</div>
                                <div className="zone-contract-history__empty">暂无历史记录</div>
                            </>
                        )}
                        <Space className="operation-fixed-btn">
                            {core?.id != null && !cancelled && canManage ? (
                                <Button type="primary" onClick={() => void openAssign()}>
                                    {core?.handlerId ? "变更指派" : "指派"}
                                </Button>
                            ) : null}
                            {core?.id != null && !cancelled && canDeal ? (
                                <>
                                    <Button
                                        type="primary"
                                        onClick={() => navigate(`/tradeManage/zoneContract/edit/${core?.id}`)}
                                    >
                                        编辑
                                    </Button>
                                    <Button aria-label="取消合同" danger onClick={() => void requestCancel()}>
                                        取消
                                    </Button>
                                </>
                            ) : null}
                            <Button onClick={() => navigate(-1)}>返回</Button>
                        </Space>
                    </div>
                </>
            )}
            <Modal
                open={cancelOpen}
                title="取消合同"
                onCancel={() => setCancelOpen(false)}
                onOk={() => void confirmCancel()}
            >
                确认取消合同吗？
            </Modal>
            <AssignDialog
                open={assignOpen}
                title="指派专员"
                info={detail}
                officers={assignOfficers}
                tips="跟进的合同数量"
                countFiled="orderCount"
                curOfficerId={handlerId}
                onClose={() => {
                    setAssignOpen(false);
                    setHandlerId(undefined);
                }}
                onCurOfficerIdChange={setHandlerId}
                onSubmit={submitAssign}
            />
            <Modal
                open={priceOpen}
                title="单价调整日志"
                width={920}
                className={`zone-contract-price-log-modal${filteredLogs.length ? "" : " zone-contract-price-log-modal--empty"}`}
                onCancel={() => setPriceOpen(false)}
                footer={<Button onClick={() => setPriceOpen(false)}>取消</Button>}
            >
                <div className="zone-contract-price-log-filter">
                    <Select
                        allowClear
                        placeholder="请选择物料搜索"
                        value={logMaterial}
                        options={goods.map(row => ({
                            value: row.goodsName || ZONE_CONTRACT_GOODS.getIntroByCode(row.goodsCode) || "--",
                            label: row.goodsName || ZONE_CONTRACT_GOODS.getIntroByCode(row.goodsCode) || "--"
                        }))}
                        onChange={value => {
                            setLogMaterial(value);
                            setLogPage(1);
                        }}
                    />
                    <Input
                        placeholder="请输入操作人姓名搜索"
                        value={logKeyword}
                        onChange={event => {
                            setLogKeyword(event.target.value);
                            setLogPage(1);
                        }}
                    />
                    <ZhDateRangePicker
                        className="zone-contract-price-log-date"
                        endPlaceholder="操作结束日期"
                        format="YYYY-MM-DD"
                        rangeSeparator="-"
                        startPlaceholder="操作开始日期"
                        type="daterange"
                        value={logDateRange}
                        valueFormat="YYYY-MM-DD"
                        onChange={(_value, formattedValue) => {
                            setLogDateRange(
                                formattedValue ? [formattedValue[0] || null, formattedValue[1] || null] : [null, null]
                            );
                            setLogPage(1);
                        }}
                    />
                    <Button icon={<SearchOutlined />} type="primary">
                        搜索
                    </Button>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                            setLogKeyword("");
                            setLogMaterial(undefined);
                            setLogDateRange([null, null]);
                            setLogPage(1);
                        }}
                    >
                        重置
                    </Button>
                </div>
                {filteredLogs.length ? (
                    <ZhTable
                        border
                        rowKey={row => String(row.id)}
                        data={filteredLogs.slice((logPage - 1) * logPageSize, logPage * logPageSize)}
                        columns={logColumns}
                        pagination={false}
                    />
                ) : (
                    <div className="zone-contract-price-log-empty-table">
                        <div className="zone-contract-price-log-empty-table__header">
                            {PRICE_LOG_EMPTY_HEADERS.map(header => (
                                <div className="zone-contract-price-log-empty-table__cell" key={header}>
                                    {header}
                                </div>
                            ))}
                        </div>
                        <div className="zone-contract-price-log-empty-table__body">
                            <div className="zone-contract-price-log-empty">
                                <img src={notDataImage} alt="暂无数据" />
                                <div>暂无数据</div>
                            </div>
                        </div>
                    </div>
                )}
                {filteredLogs.length ? (
                    <div className="zone-contract-price-log-pager">
                        <span className="zone-contract-price-log-total">共{filteredLogs.length}条数据</span>
                        <div className="zone-contract-price-log-controls">
                            <Pagination
                                className="zone-contract-price-log-pagination"
                                current={logPage}
                                pageSize={logPageSize}
                                total={filteredLogs.length}
                                pageSizeOptions={[10, 20, 30, 50, 100]}
                                showSizeChanger
                                onChange={setLogPage}
                                onShowSizeChange={(_current, size) => {
                                    setLogPageSize(size);
                                    setLogPage(size === 0 ? 1 : Math.min(logPage, Math.max(1, Math.ceil(filteredLogs.length / size))));
                                }}
                            />
                            <span className="zone-contract-price-log-jumper">
                                转到
                                <Input
                                    aria-label="跳转页码"
                                    value={String(logPage)}
                                    onChange={event => {
                                        const nextPage = Number(event.target.value);
                                        if (Number.isInteger(nextPage) && nextPage > 0) {
                                            setLogPage(Math.min(nextPage, Math.max(1, Math.ceil(filteredLogs.length / logPageSize))));
                                        }
                                    }}
                                />
                                页
                            </span>
                        </div>
                    </div>
                ) : null}
            </Modal>
        </section>
    );
}

export default ZoneContractDetailPage;
